import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { PlusIcon, PencilIcon, TrashIcon, XMarkIcon, FunnelIcon, TagIcon } from '@heroicons/react/24/outline';
import ManageStatuses from './ManageStatuses';

const ProductConfig = () => {
  const { token } = useContext(AppContext);
  const [products, setProducts] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    tagIds: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState('services');

  // Filter state
  const [filterTagId, setFilterTagId] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token, filterTagId]);

  const fetchData = async () => {
    try {
      // Build URL with optional tag filter
      let productsUrl = '/api/products';
      if (filterTagId) {
        productsUrl += `?tagId=${filterTagId}`;
      }

      const [productsRes, tagsRes] = await Promise.all([
        fetch(productsUrl, { headers: { Authorization: token } }),
        fetch('/api/statuses?categoryId=2', { headers: { Authorization: token } })
      ]);
      if (productsRes.ok) setProducts(await productsRes.json());
      if (tagsRes.ok) {
        const tagData = await tagsRes.json();
        setTags(tagData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleOpenForm = (product = null) => {
    if (product) {
      setSelectedProduct(product);
      setFormData({
        name: product.name || '',
        price: product.price || '',
        description: product.description || '',
        tagIds: product.tags ? product.tags.map(t => t.id) : []
      });
    } else {
      setSelectedProduct(null);
      setFormData({
        name: '',
        price: '',
        description: '',
        tagIds: []
      });
    }
    setIsFormOpen(true);
    setError('');
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedProduct(null);
    setError('');
  };

  const handleTagToggle = (tagId) => {
    setFormData(prev => ({
      ...prev,
      tagIds: prev.tagIds.includes(tagId)
        ? prev.tagIds.filter(id => id !== tagId)
        : [...prev.tagIds, tagId]
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const url = selectedProduct ? `/api/products/${selectedProduct.id}` : '/api/products';
      const method = selectedProduct ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          name: formData.name,
          price: parseFloat(formData.price) || 0,
          description: formData.description,
          tagIds: formData.tagIds
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.message || 'Failed to save service');
      }

      const savedProduct = await response.json();

      if (selectedProduct) {
        setProducts((prev) => prev.map((p) => (p.id === savedProduct.id ? savedProduct : p)));
      } else {
        setProducts((prev) => [...prev, savedProduct]);
      }

      handleCloseForm();
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      const response = await fetch(`/api/products/${id}`, {
        method: 'DELETE',
        headers: { Authorization: token },
      });

      if (response.ok) {
        setProducts((prev) => prev.filter((p) => p.id !== id));
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete service');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to delete service');
    }
  };

  const clearFilters = () => {
    setFilterTagId('');
  };

  const activeFilterCount = filterTagId ? 1 : 0;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Services</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage the services and products offered to the community.
          </p>
        </div>
        {activeTab === 'services' && (
          <div className="mt-4 sm:mt-0">
            <button
              type="button"
              onClick={() => handleOpenForm()}
              className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
              Add Service
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-4 border-b border-gray-200">
        <nav className="flex -mb-px space-x-6">
          <button
            onClick={() => { setActiveTab('services'); fetchData(); }}
            className={`pb-3 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'services'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Services
          </button>
          <button
            onClick={() => setActiveTab('tags')}
            className={`pb-3 px-1 text-sm font-medium border-b-2 ${
              activeTab === 'tags'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <TagIcon className="h-4 w-4" />
              Manage Tags
            </span>
          </button>
        </nav>
      </div>

      {activeTab === 'tags' && (
        <div className="mt-6">
          <ManageStatuses />
        </div>
      )}

      {activeTab === 'services' && (
      <>


      {/* Filter Bar */}
      <div className="mt-6 space-y-4">
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ${
              showFilters || activeFilterCount > 0
                ? 'bg-indigo-50 text-indigo-700 ring-indigo-200'
                : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-5 w-5 mr-1" aria-hidden="true" />
            Filter by Tag
            {activeFilterCount > 0 && (
              <span className="ml-1.5 rounded-full bg-indigo-600 px-1.5 py-0.5 text-xs text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-gray-50 rounded-lg p-4 ring-1 ring-gray-200">
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => setFilterTagId(filterTagId === tag.id.toString() ? '' : tag.id.toString())}
                  className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
                    filterTagId === tag.id.toString()
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <TagIcon className="h-4 w-4 mr-1" />
                  {tag.name}
                </button>
              ))}
            </div>
            {activeFilterCount > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  Clear filter
                </button>
              </div>
            )}
          </div>
        )}

        {products.length > 0 && (
          <p className="text-sm text-gray-500">
            Showing {products.length} service{products.length !== 1 ? 's' : ''}
            {filterTagId && ` with tag "${tags.find(t => t.id.toString() === filterTagId)?.name}"`}
          </p>
        )}
      </div>

      {/* Products Table */}
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            {products.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow ring-1 ring-gray-200">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No services</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {filterTagId ? 'No services match your filter.' : 'Get started by creating a new service.'}
                </p>
                <div className="mt-6">
                  {filterTagId ? (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    >
                      Clear Filter
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleOpenForm()}
                      className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
                    >
                      <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                      Add Service
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Name
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Price
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Tags
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {products.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900">{product.name}</div>
                          {product.description && (
                            <div className="text-gray-500">{product.description}</div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
                          ${parseFloat(product.price).toFixed(2)}
                        </td>
                        <td className="px-3 py-4 text-sm">
                          <div className="flex flex-wrap gap-1">
                            {product.tags && product.tags.length > 0 ? (
                              product.tags.map((tag) => (
                                <span
                                  key={tag.id}
                                  className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700"
                                >
                                  {tag.name}
                                </span>
                              ))
                            ) : (
                              <span className="text-gray-400 italic">No tags</span>
                            )}
                          </div>
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => handleOpenForm(product)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(product.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <TrashIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseForm} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <h3 className="text-lg font-semibold leading-6 text-gray-900">
                  {selectedProduct ? 'Edit Service' : 'Add New Service'}
                </h3>
                <form onSubmit={handleSave} className="mt-4 space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                      Price
                    </label>
                    <div className="relative mt-1 rounded-md shadow-sm">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        name="price"
                        id="price"
                        required
                        min="0"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="mt-1 block w-full rounded-md border-gray-300 pl-7 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      name="description"
                      id="description"
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Click to toggle tags for this service
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {tags.length > 0 ? (
                        tags.map((tag) => (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => handleTagToggle(tag.id)}
                            className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                              formData.tagIds.includes(tag.id)
                                ? 'bg-indigo-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {formData.tagIds.includes(tag.id) && (
                              <XMarkIcon className="h-3 w-3 mr-1" />
                            )}
                            {tag.name}
                          </button>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          No tags available. Create tags in Settings &gt; Manage Service Tags.
                        </p>
                      )}
                    </div>
                  </div>

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 sm:col-start-2"
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseForm}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default ProductConfig;
